import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';
import EmptyState from '../components/EmptyState';
import { messagingApi, ThreadSummary } from '../api';
import { Card, IconBadge } from '../components/ui';

const Messages: React.FC = () => {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setError('');
      setIsLoading(true);
      try {
        const data = await messagingApi.listThreads();
        setThreads(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load messages');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  return (
    <Layout>
      <div className="pb-20 space-y-6">
        <div className="flex items-center space-x-3">
            <IconBadge tone="gold"><MessageCircle /></IconBadge>
            <div>
              <h1 className="text-xl font-bold text-white">Messages</h1>
              <p className="text-sm text-green-100/60">Where the excuses begin before the round.</p>
            </div>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : threads.length === 0 ? (
          <Card padding="none" className="rounded-3xl">
            <EmptyState
              image="/images/empty-messages.png"
              title="No messages yet"
              subtitle="Start a chat from a golfer's profile to talk trash and settle bets."
            />
          </Card>
        ) : (
          <Card padding="sm">
            {threads.map((t) => (
              <Link
                key={t.thread_id}
                to={`/messages/thread/${t.thread_id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition"
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center overflow-hidden">
                    {t.other_user.avatar_url ? (
                      <img src={t.other_user.avatar_url} alt={t.other_user.full_name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-semibold">{t.other_user.full_name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{t.other_user.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{t.last_message || 'Say hi 👋'}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </Link>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Messages;
